import type { ActivityModule, ActivityContext, ActivityHandle } from '../../core/activity';
import '../../shared/tens-complement.css';
import levelsData from './levels.json';

interface LevelConfig {
  randomValues: boolean;
  shuffleOperands: boolean;
  numberOfSubLevels: number;
  numberOfAdditions: number;
  numberOfExtraCards: number;
  findBothNumbers: boolean;
  minimumFirstValue: number;
  maximumFirstValue: number;
}

interface Level {
  level: number;
  objective: string;
  difficulty: number;
  data: LevelConfig[];
}

interface LevelsFile {
  levels: Level[];
}

interface Card {
  id: number;
  value: number;
  hidden: boolean;
}

interface Equation {
  rowIndex: number;
  leftValue: number | null;
  rightValue: number | null;
  leftLocked: boolean;
  rightLocked: boolean;
  status: 'pending' | 'ok' | 'err';
}

const LEVELS = (levelsData as LevelsFile).levels;

function shuffle<T>(arr: T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export const tensComplementFind: ActivityModule = {
  id: 'tens_complement_find',
  title: 'Encontre o complemento de 10',
  description: 'Encontre o número que falta para completar 10. Use os cartões disponíveis.',
  ageMin: 7,
  ageMax: 10,
  difficulty: 4,
  iconPath: 'assets/gcompris-AGPL/tens_complement_find/icon.svg',
  category: 'aritmetica',
  totalLevels: LEVELS.length,
  mount,
};

function mount(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }
  const config = level.data[0]!;
  const totalSubs = config.numberOfSubLevels;
  let currentSub = 0;
  let equations: Equation[] = [];
  let pool: Card[] = [];
  let selectedCardId: number | null = null;
  let locked = false;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Encontre o complemento de 10</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage tens">
      <div class="feedback" aria-live="polite"></div>
      <div class="tens__progress"></div>
      <p class="tens__hint">Selecione um cartão e clique num <strong>?</strong> para colocá-lo lá. Use os números que somam <strong>10</strong>.</p>
      <div class="tens__equations"></div>
      <div class="tens__pool" role="group" aria-label="Cartões disponíveis"></div>
      <div class="tens__actions">
        <button class="btn btn--primary tens__ok" type="button" style="visibility:hidden">OK</button>
      </div>
    </section>
  `;
  ctx.root.appendChild(page);

  const backBtn = page.querySelector<HTMLButtonElement>('.activity-bar__back')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const equationsEl = page.querySelector<HTMLElement>('.tens__equations')!;
  const poolEl = page.querySelector<HTMLElement>('.tens__pool')!;
  const progressEl = page.querySelector<HTMLElement>('.tens__progress')!;
  const okBtn = page.querySelector<HTMLButtonElement>('.tens__ok')!;

  backBtn.addEventListener('click', () => ctx.onExit());
  okBtn.addEventListener('click', validate);

  initSubLevel();

  function initSubLevel(): void {
    equations = [];
    pool = [];
    selectedCardId = null;
    locked = false;

    const usedFirstOps: number[] = [];
    const cardsValues: number[] = [];
    const nAdd = Math.min(3, config.numberOfAdditions);

    for (let i = 0; i < nAdd; i++) {
      let v: number;
      let tries = 10;
      do {
        v = randInt(config.minimumFirstValue, config.maximumFirstValue);
        tries--;
      } while (usedFirstOps.includes(v) && tries > 0);
      usedFirstOps.push(v);

      const complement = 10 - v;
      cardsValues.push(complement);
      if (config.findBothNumbers) cardsValues.push(v);

      let leftLocked = true, rightLocked = true;
      let leftValue: number | null = v, rightValue: number | null = null;
      if (config.findBothNumbers) {
        leftValue = null;
        rightValue = null;
        leftLocked = false;
        rightLocked = false;
      } else {
        if (config.shuffleOperands && Math.random() < 0.5) {
          leftValue = null;
          rightValue = v;
          leftLocked = false;
          rightLocked = true;
        } else {
          leftValue = v;
          rightValue = null;
          leftLocked = true;
          rightLocked = false;
        }
      }
      equations.push({ rowIndex: i, leftValue, rightValue, leftLocked, rightLocked, status: 'pending' });
    }

    let nExtra = config.numberOfExtraCards;
    const maxCards = 6;
    if (cardsValues.length + nExtra > maxCards) nExtra = Math.max(0, maxCards - cardsValues.length);
    for (let i = 0; i < nExtra; i++) cardsValues.push(randInt(1, 9));

    pool = shuffle(cardsValues).map((value, id) => ({ id, value, hidden: false }));
    equations = shuffle(equations).map((e, i) => ({ ...e, rowIndex: i }));

    render();
  }

  function render(): void {
    progressEl.textContent = `${currentSub + 1} / ${totalSubs}`;
    equationsEl.innerHTML = '';
    for (const eq of equations) {
      const row = document.createElement('div');
      row.className = `tens__equation tens__equation--${eq.status}`;
      row.dataset.row = String(eq.rowIndex);
      row.appendChild(makeOperandEl(eq, 'left'));
      row.appendChild(opEl('+'));
      row.appendChild(makeOperandEl(eq, 'right'));
      row.appendChild(opEl('='));
      const ten = document.createElement('div');
      ten.className = 'tens__fixed';
      ten.textContent = '10';
      row.appendChild(ten);
      if (eq.status !== 'pending') {
        const mark = document.createElement('div');
        mark.className = 'tens__feedback-mark';
        mark.textContent = eq.status === 'ok' ? '✓' : '✗';
        mark.style.color = eq.status === 'ok' ? 'var(--color-success)' : '#ef4444';
        row.appendChild(mark);
      }
      equationsEl.appendChild(row);
    }
    poolEl.innerHTML = '';
    for (const card of pool) {
      const el = document.createElement('button');
      el.className = 'tens__card';
      if (card.hidden) el.classList.add('tens__card--hidden');
      if (selectedCardId === card.id) el.classList.add('tens__card--selected');
      el.type = 'button';
      el.dataset.cardId = String(card.id);
      el.textContent = String(card.value);
      el.addEventListener('click', () => onCardClick(card.id));
      poolEl.appendChild(el);
    }
    okBtn.style.visibility = allFilled() ? 'visible' : 'hidden';
  }

  function makeOperandEl(eq: Equation, side: 'left' | 'right'): HTMLElement {
    const value = side === 'left' ? eq.leftValue : eq.rightValue;
    const locked = side === 'left' ? eq.leftLocked : eq.rightLocked;
    if (locked) {
      const el = document.createElement('div');
      el.className = 'tens__fixed';
      el.textContent = String(value);
      return el;
    }
    const el = document.createElement('button');
    el.type = 'button';
    el.className = value !== null ? 'tens__slot tens__slot--filled' : 'tens__slot';
    el.textContent = value !== null ? String(value) : '?';
    el.dataset.row = String(eq.rowIndex);
    el.dataset.side = side;
    el.addEventListener('click', () => onSlotClick(eq.rowIndex, side));
    return el;
  }

  function opEl(text: string): HTMLElement {
    const el = document.createElement('div');
    el.className = 'tens__op';
    el.textContent = text;
    return el;
  }

  function onCardClick(cardId: number): void {
    if (locked) return;
    const card = pool.find((c) => c.id === cardId);
    if (!card || card.hidden) return;
    selectedCardId = selectedCardId === cardId ? null : cardId;
    ctx.audio.play('click');
    render();
  }

  function onSlotClick(rowIndex: number, side: 'left' | 'right'): void {
    if (locked) return;
    const eq = equations.find((e) => e.rowIndex === rowIndex);
    if (!eq) return;
    const currentValue = side === 'left' ? eq.leftValue : eq.rightValue;
    if (currentValue !== null) {
      const card = pool.find((c) => c.hidden && c.value === currentValue);
      if (card) card.hidden = false;
      if (side === 'left') eq.leftValue = null;
      else eq.rightValue = null;
      eq.status = 'pending';
      ctx.audio.play('click');
      render();
      return;
    }
    if (selectedCardId === null) return;
    const card = pool.find((c) => c.id === selectedCardId);
    if (!card || card.hidden) return;
    if (side === 'left') eq.leftValue = card.value;
    else eq.rightValue = card.value;
    card.hidden = true;
    selectedCardId = null;
    ctx.audio.play('click');
    render();
  }

  function allFilled(): boolean {
    return equations.every((e) => e.leftValue !== null && e.rightValue !== null);
  }

  function validate(): void {
    if (locked || !allFilled()) return;
    let allOk = true;
    for (const eq of equations) {
      const sum = (eq.leftValue ?? 0) + (eq.rightValue ?? 0);
      eq.status = sum === 10 ? 'ok' : 'err';
      if (sum !== 10) allOk = false;
    }
    render();
    if (allOk) {
      locked = true;
      ctx.audio.play('correct');
      showFeedback('✓ Tudo certo!', 'ok');
      window.setTimeout(() => {
        currentSub++;
        if (currentSub >= totalSubs) finishLevel();
        else initSubLevel();
      }, 1000);
    } else {
      ctx.audio.play('wrong');
      showFeedback('Corrija os erros.', 'err');
    }
  }

  function showFeedback(text: string, kind: 'ok' | 'err'): void {
    feedback.textContent = text;
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 1000);
  }

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level!.level, totalSubs);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level!.level} concluído!</h2>
      <p class="activity-finish__text">Você completou ${totalSubs} desafios.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level!.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/tens_complement_find/${level!.level + 1}`;
      });
    }
  }

  return {
    unmount(): void {
      ctx.root.innerHTML = '';
    },
  };
}
